import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import { TABLE_ROW_HEIGHT } from '../../../../consts';
import type {
  TableProperty,
  TableSingleView,
} from '../../../../table-view-manager';
import type { VirtualTableViewUILogic } from '../../../table-view-ui-logic';

type PreviewRow = {
  height: number;
  rowId: string;
  text: string;
};

export class DataViewColumnPreview extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    virtual-data-view-column-preview {
      pointer-events: none;
      display: block;
      position: fixed;
      font-family: var(--streamlain-font-family);
      opacity: 0.5;
    }

    .column-preview-surface {
      transform-origin: top left;
    }
  `;

  get tableViewManager(): TableSingleView {
    return this.tableViewLogic.view;
  }

  private getPreviewRows(): PreviewRow[] {
    const visibleCells = Array.from(
      this.container.querySelectorAll<HTMLElement>(
        `affine-database-virtual-cell-container[data-column-id="${this.column.id}"]`
      )
    ).sort(
      (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
    );
    const rows = visibleCells
      .map(cell => {
        const rowId = cell.dataset.rowId;
        if (!rowId) {
          return;
        }
        return {
          height: cell.parentElement?.clientHeight || TABLE_ROW_HEIGHT,
          rowId,
          text: cell.textContent ?? '',
        };
      })
      .filter((row): row is PreviewRow => !!row);
    if (rows.length > 0) {
      return rows;
    }

    return this.tableViewManager
      .getBoundedRowsWindow()
      .rows.slice(0, 20)
      .map(row => ({
        height: TABLE_ROW_HEIGHT,
        rowId: row.rowId,
        text: '',
      }));
  }

  private renderGroup(rows: PreviewRow[]) {
    const scale = this.viewportScale;
    const surfaceStyle = styleMap({
      transform: scale === 1 ? undefined : `scale(${scale})`,
      width: `${this.column.width$.value}px`,
    });

    return html`
      <div class="column-preview-surface" style="${surfaceStyle}">
        <div
          style="background-color: var(--streamlain-background-primary-color);box-shadow: var(--streamlain-shadow-2);"
        >
          <virtual-database-header-column
            .tableViewLogic="${this.tableViewLogic}"
            .column="${this.column}"
          ></virtual-database-header-column>
          ${repeat(
            rows,
            row => row.rowId,
            row => {
              const style = styleMap({
                boxSizing: 'border-box',
                height: row.height + 'px',
                lineHeight: '24px',
                overflow: 'hidden',
                padding: '8px 12px',
                whiteSpace: 'pre-wrap',
              });
              return html`<div>
                <div style="${style}">${row.text}</div>
              </div>`;
            }
          )}
        </div>
      </div>
    `;
  }

  override render() {
    return this.renderGroup(this.getPreviewRows());
  }

  @property({ attribute: false })
  accessor column!: TableProperty;

  @property({ attribute: false })
  accessor container!: HTMLElement;

  @property({ attribute: false })
  accessor tableViewLogic!: VirtualTableViewUILogic;

  @property({ attribute: false })
  accessor viewportScale = 1;
}

declare global {
  interface HTMLElementTagNameMap {
    'virtual-data-view-column-preview': DataViewColumnPreview;
  }
}
